import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { UserListTable } from "@/components/user/table";
import { useGetAllUsers } from "@/hooks/api/users/useGetAllUsers";
import { useDebounce } from "@/hooks/useDebounce";
import { AppPage } from "@/layouts/page";
import { AttributionFilters } from "@/types";

export function UsersPage() {
  let [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState<AttributionFilters>({});

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const initialPage = searchParams.get("page")
    ? parseInt(searchParams.get("page")!)
    : 1;

  const [pageParams, setPageParams] = useState({
    page: initialPage,
    limit: 10,
  });

  const { data: users, isFetching: isUsersLoading } = useGetAllUsers({
    ...pageParams,
    ...filters,
    searchTerm: debouncedSearchTerm,
  });

  useEffect(() => {
    const urlPage = searchParams.get("page");

    if (urlPage) {
      const page = parseInt(urlPage);

      setPageParams((prev) => ({ ...prev, page }));
    } else {
      setPageParams((prev) => ({ ...prev, page: 1 }));
    }
  }, [searchParams]);

  const handleLimitChange = (limit: number) => {
    setPageParams((prev) => ({ ...prev, limit }));
    handlePageChange(1);
  };

  const handlePageChange = (newPage: number) => {
    searchParams.set("page", newPage.toString());
    setSearchParams(searchParams);
  };

  const handleSearchTermChange = (newSearchTerm: string) => {
    setSearchTerm(newSearchTerm);
  };

  const handleFiltersChange = (next: AttributionFilters) => {
    setFilters(next);
    handlePageChange(1);
  };

  return (
    <AppPage subtitle={users ? `${users.total.toLocaleString()} users in total` : undefined} title="Users">
      <UserListTable
        changePage={handlePageChange}
        filters={filters}
        handleSearchTermChange={handleSearchTermChange}
        limit={pageParams.limit}
        onLimitChange={handleLimitChange}
        isLoading={isUsersLoading}
        items={users?.data ?? []}
        page={pageParams.page ?? 1}
        searchTerm={searchTerm}
        totalUsers={users?.total ?? 0}
        onFiltersChange={handleFiltersChange}
      />
    </AppPage>
  );
}
